import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import SaveRounded from '@mui/icons-material/SaveRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import DataObjectRounded from '@mui/icons-material/DataObjectRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import ClearRounded from '@mui/icons-material/ClearRounded';
import CloudUploadRounded from '@mui/icons-material/CloudUploadRounded';
import PageHeader from '../components/common/PageHeader';
import storeLabel from '../utils/storeLabel';
import { useStores } from '../context/StoresContext';
import {
  getFields,
  createField,
  deleteField,
  updateFieldMappings,
  copyStoreMappings,
  bulkDeleteFields,
  bulkUpdateFieldCategory,
  pushFieldDefinitions,
  deleteFieldDefinitions,
} from '../services/fieldsApi';
import { getStoreMetafieldDefinitions } from '../services/shopifyApi';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'rich_text', label: 'Rich Text' },
  { value: 'image', label: 'Image' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'json', label: 'JSON' },
  { value: 'color', label: 'Color' },
  { value: 'dimension', label: 'Dimension' },
  { value: 'weight', label: 'Weight' },
  { value: 'url', label: 'URL' },
];

const FIELD_CATEGORIES = [
  { value: '', label: 'None' },
  { value: 'Static', label: 'Static' },
  { value: 'Fabric Pattern Field', label: 'Fabric Pattern Field' },
  { value: 'Finish Type Field', label: 'Finish Type Field' },
  { value: 'Installation Field', label: 'Installation Field' },
  { value: 'Certification Field', label: 'Certification Field' },
];

const MEASUREMENT_UNITS = [
  { value: '', label: 'None' },
  { value: 'yards', label: 'Yards' },
  { value: 'sq_feet', label: 'Sq Feet' },
  { value: 'lbs', label: 'Lbs' },
  { value: 'inches', label: 'Inches' },
  { value: 'meters', label: 'Meters' },
  { value: 'sq_meters', label: 'Sq Meters' },
  { value: 'kgs', label: 'Kgs' },
  { value: 'cm', label: 'CM' },
];

const typeColors = {
  text: '#6C63FF',
  number: '#00D9A6',
  rich_text: '#FF6B6B',
  image: '#FF9800',
  boolean: '#2196F3',
  date: '#9C27B0',
  json: '#607D8B',
  color: '#E91E63',
  dimension: '#795548',
  weight: '#009688',
  url: '#3F51B5',
};
const metafieldFilter = createFilterOptions({ matchFrom: 'any', limit: 20 });

const Fields = () => {
  const { activeStores, mainStore } = useStores();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('text');
  const [newCategory, setNewCategory] = useState('');
  const [newMeasurementUnit, setNewMeasurementUnit] = useState('');
  const [newPerVariant, setNewPerVariant] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  // Track saving per field+store combo: "fieldId-storeId"
  const [savingCell, setSavingCell] = useState(null);
  const [error, setError] = useState('');
  const [metafieldOptions, setMetafieldOptions] = useState({});
  const [loadingStoreOptions, setLoadingStoreOptions] = useState(null);
  const fetchTimers = useRef({});

  // Local state for mapping edits (field_id -> { store_id -> metafield_key })
  const [mappingEdits, setMappingEdits] = useState({});

  // Search & filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Selection state
  const [selectedFields, setSelectedFields] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('');
  const [pushingDefinitions, setPushingDefinitions] = useState(false);
  const [deletingDefinitions, setDeletingDefinitions] = useState(false);
  const [pushTargetStoreId, setPushTargetStoreId] = useState('');

  // Filtered fields
  const filteredFields = useMemo(() => {
    let result = fields;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((f) => f.name.toLowerCase().includes(q));
    }
    if (filterCategory) {
      result = result.filter((f) => f.category === filterCategory);
    }
    return result;
  }, [fields, searchQuery, filterCategory]);

  const filteredSelectableIds = useMemo(() => filteredFields.map((f) => f.id), [filteredFields]);

  const allSelected = filteredSelectableIds.length > 0 && filteredSelectableIds.every((id) => selectedFields.has(id));
  const someSelected = filteredSelectableIds.some((id) => selectedFields.has(id));

  const toggleFieldSelection = (id) => {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedFields(new Set());
    } else {
      setSelectedFields(new Set(filteredSelectableIds));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFields.size === 0) return;
    if (!window.confirm(`Delete ${selectedFields.size} field(s)? This cannot be undone.`)) return;
    setBulkProcessing(true);
    try {
      await bulkDeleteFields(Array.from(selectedFields));
      setSelectedFields(new Set());
      await fetchFields();
    } catch (err) {
      console.error('Bulk delete failed:', err);
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkUpdateCategory = async () => {
    if (selectedFields.size === 0) return;
    setBulkProcessing(true);
    try {
      await bulkUpdateFieldCategory(Array.from(selectedFields), bulkCategory);
      setSelectedFields(new Set());
      setBulkCategory('');
      await fetchFields();
    } catch (err) {
      console.error('Bulk category update failed:', err);
    } finally {
      setBulkProcessing(false);
    }
  };

  const handlePushDefinitions = async () => {
    setPushingDefinitions(true);
    try {
      const targetStoreIds = pushTargetStoreId === 'all'
        ? activeStores.map((store) => store.id)
        : (pushTargetStoreId ? [Number(pushTargetStoreId)] : []);
      const data = await pushFieldDefinitions(targetStoreIds);
      const targetLabel = pushTargetStoreId === 'all'
        ? `${activeStores.length} active store(s)`
        : (storeLabel(activeStores.find((store) => String(store.id) === pushTargetStoreId)) || 'selected store');
      alert(
        `Attempted ${data.attempted_count} definitions for ${targetLabel}.\n` +
        `${data.created_count} created, ${data.existing_count} already existed, ${data.error_count} errors.`
      );
    } catch (err) {
      alert(`Failed to push metafield definitions: ${err.response?.data?.message || err.message}`);
    } finally {
      setPushingDefinitions(false);
    }
  };

  const handleDeleteDefinitions = async () => {
    const targetStoreIds = pushTargetStoreId === 'all'
      ? activeStores.map((store) => store.id)
      : (pushTargetStoreId ? [Number(pushTargetStoreId)] : []);
    const targetLabel = pushTargetStoreId === 'all'
      ? `${activeStores.length} active store(s)`
      : (storeLabel(activeStores.find((store) => String(store.id) === pushTargetStoreId)) || 'selected store');

    if (!window.confirm(`Delete mapped metafield definitions from ${targetLabel}?`)) {
      return;
    }

    setDeletingDefinitions(true);
    try {
      const data = await deleteFieldDefinitions(targetStoreIds);
      alert(
        `Attempted ${data.attempted_count} definitions for ${targetLabel}.\n` +
        `${data.deleted_count} deleted, ${data.missing_count} already missing, ${data.error_count} errors.`
      );
    } catch (err) {
      alert(`Failed to delete metafield definitions: ${err.response?.data?.message || err.message}`);
    } finally {
      setDeletingDefinitions(false);
    }
  };

  const fetchFields = useCallback(async () => {
    try {
      const { fields: fieldList } = await getFields();
      setFields(fieldList);

      const edits = {};
      for (const f of fieldList) {
        edits[f.id] = {};
        for (const m of f.store_mappings) {
          edits[f.id][m.store_id] = m.metafield_key;
        }
      }
      setMappingEdits(edits);
    } catch (err) {
      console.error('Failed to fetch fields:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFields();
  }, [fetchFields]);

  useEffect(() => {
    if (pushTargetStoreId) return;
    if (mainStore?.id) {
      setPushTargetStoreId(String(mainStore.id));
      return;
    }
    if (activeStores.length > 0) {
      setPushTargetStoreId(String(activeStores[0].id));
    }
  }, [activeStores, mainStore, pushTargetStoreId]);

  // Clear any pending debounce timers on unmount
  useEffect(() => () => {
    Object.values(fetchTimers.current || {}).forEach((t) => clearTimeout(t));
  }, []);

  const handleCreate = async () => {
    if (!newName.trim()) {
      setError('Field name is required');
      return;
    }

    setCreating(true);
    setError('');
    try {
      await createField(newName.trim(), newType, newCategory, newMeasurementUnit || null, newPerVariant);
      setDialogOpen(false);
      setNewName('');
      setNewType('text');
      setNewCategory('');
      setNewMeasurementUnit('');
      setNewPerVariant(false);
      await fetchFields();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.message;
      setError(msg || 'Failed to create field');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm('Delete this field? This cannot be undone.')) return;

    setDeleting(fieldId);
    try {
      await deleteField(fieldId);
      await fetchFields();
    } catch (err) {
      console.error('Failed to delete field:', err);
    } finally {
      setDeleting(null);
    }
  };

  const handleMappingChange = (fieldId, storeId, value) => {
    setMappingEdits((prev) => ({
      ...prev,
      [fieldId]: { ...prev[fieldId], [storeId]: value },
    }));
    loadMetafieldOptions(storeId, value);
  };

  const loadMetafieldOptions = useCallback((storeId, search = '') => {
    if (!storeId) return;
    const trimmed = (search || '').trim();
    // Avoid spamming Shopify for very short strings
    if (trimmed.length > 0 && trimmed.length < 2) return;

    if (fetchTimers.current[storeId]) {
      clearTimeout(fetchTimers.current[storeId]);
    }

    fetchTimers.current[storeId] = setTimeout(async () => {
      setLoadingStoreOptions(storeId);
      try {
        const { definitions } = await getStoreMetafieldDefinitions(storeId, trimmed);
        const options = (definitions || []).map((d) => ({
          value: `${d.namespace}.${d.key}`,
          label: d.name || `${d.namespace}.${d.key}`,
          namespace: d.namespace,
          type: d.type,
        }));
        setMetafieldOptions((prev) => ({ ...prev, [storeId]: options }));
      } catch (err) {
        console.error('Failed to fetch metafield definitions', err);
      } finally {
        setLoadingStoreOptions(null);
      }
    }, 250);
  }, []);

  // Save a single store mapping for a field
  const handleSaveCell = async (fieldId, storeId) => {
    const key = mappingEdits[fieldId]?.[storeId] ?? '';
    if (key && !key.includes('.')) {
      alert('Metafield key must include a namespace and key, e.g., custom.seat_depth');
      return;
    }
    const cellKey = `${fieldId}-${storeId}`;

    setSavingCell(cellKey);
    try {
      await updateFieldMappings(fieldId, [{ store_id: Number(storeId), metafield_key: key }]);
      // Update the saved state in fields so the change is no longer "dirty"
      setFields((prev) =>
        prev.map((f) => {
          if (f.id !== fieldId) return f;
          const existingMapping = f.store_mappings.find((m) => m.store_id === Number(storeId));
          if (existingMapping) {
            return {
              ...f,
              store_mappings: f.store_mappings.map((m) =>
                m.store_id === Number(storeId) ? { ...m, metafield_key: key } : m
              ),
            };
          }
          return {
            ...f,
            store_mappings: [...f.store_mappings, { store_id: Number(storeId), metafield_key: key }],
          };
        })
      );
    } catch (err) {
      console.error('Failed to save mapping:', err);
    } finally {
      setSavingCell(null);
    }
  };

  const handleCopyMainToAll = async (field) => {
    if (!mainStore) {
      alert('Set a main store first to copy mappings.');
      return;
    }
    const key = mappingEdits[field.id]?.[mainStore.id] ?? '';
    if (!key) {
      alert('Main store key is empty; nothing to copy.');
      return;
    }
    if (!key.includes('.')) {
      alert('Metafield key must include a namespace and key, e.g., custom.fabric_content');
      return;
    }
    const bulkKey = `bulk-${field.id}`;
    setSavingCell(bulkKey);
    try {
      const mappings = activeStores.map((store) => ({ store_id: store.id, metafield_key: key }));
      await updateFieldMappings(field.id, mappings);
      setFields((prev) =>
        prev.map((f) => {
          if (f.id !== field.id) return f;
          return {
            ...f,
            store_mappings: activeStores.map((s) => ({ store_id: s.id, metafield_key: key })),
          };
        })
      );
      setMappingEdits((prev) => ({
        ...prev,
        [field.id]: Object.fromEntries(activeStores.map((s) => [s.id, key])),
      }));
    } catch (err) {
      console.error('Failed to copy mapping to all stores:', err);
    } finally {
      setSavingCell(null);
    }
  };

  // "Copy from store" menu: { anchorEl, targetStore }
  const [copyMenu, setCopyMenu] = useState(null);
  const [copyingStore, setCopyingStore] = useState(null);

  const handleCopyFromStore = async (sourceStore, targetStore) => {
    setCopyMenu(null);
    setCopyingStore(targetStore.id);
    try {
      const res = await copyStoreMappings(sourceStore.id, targetStore.id);
      await fetchFields();
      const src = storeLabel(sourceStore);
      const tgt = storeLabel(targetStore);
      const skipped = res.alreadySet ? ` ${res.alreadySet} field(s) already had a key and were kept.` : '';
      alert(`Copied ${res.copied} field key(s) from ${src} to ${tgt}.${skipped}`);
    } catch (err) {
      alert(`Copy failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setCopyingStore(null);
    }
  };

  // Check if a single cell has unsaved changes
  const cellHasChanges = (field, storeId) => {
    const editValue = mappingEdits[field.id]?.[storeId] ?? '';
    const savedMapping = field.store_mappings.find((m) => m.store_id === storeId);
    const savedValue = savedMapping?.metafield_key ?? '';
    return editValue !== savedValue;
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Fields" subtitle="Define and manage metafield structures" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Fields"
        subtitle="Define and manage metafield structures"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              select
              size="small"
              label="Push To"
              value={pushTargetStoreId}
              onChange={(e) => setPushTargetStoreId(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="all">All Active Stores</MenuItem>
              {activeStores.map((store) => (
                <MenuItem key={store.id} value={String(store.id)}>
                  {storeLabel(store)}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              startIcon={pushingDefinitions ? <CircularProgress size={18} color="inherit" /> : <CloudUploadRounded />}
              onClick={handlePushDefinitions}
              disabled={pushingDefinitions || activeStores.length === 0 || !pushTargetStoreId}
            >
              {pushingDefinitions ? 'Pushing...' : 'Push Definitions'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={deletingDefinitions ? <CircularProgress size={18} color="inherit" /> : <DeleteOutlineRounded />}
              onClick={handleDeleteDefinitions}
              disabled={deletingDefinitions || activeStores.length === 0 || !pushTargetStoreId}
            >
              {deletingDefinitions ? 'Deleting...' : 'Delete Definitions'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => setDialogOpen(true)}
            >
              Add Field
            </Button>
          </Box>
        }
      />

      {/* Search & Filter Toolbar */}
      {fields.length > 0 && (
        <Card sx={{ mb: 2, p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search fields..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 220 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              select
              size="small"
              label="Category"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {FIELD_CATEGORIES.filter((c) => c.value).map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </TextField>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {filteredFields.length} of {fields.length} field{fields.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Card>
      )}

      {/* Bulk Actions Bar */}
      {selectedFields.size > 0 && (
        <Card sx={{ mb: 2, p: 1.5, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={toggleSelectAll}
            />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedFields.size} field{selectedFields.size !== 1 ? 's' : ''} selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 2 }}>
              <TextField
                select
                size="small"
                label="Set Category"
                value={bulkCategory}
                onChange={(e) => setBulkCategory(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                {FIELD_CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label || 'None'}</MenuItem>
                ))}
              </TextField>
              <Button
                size="small"
                variant="outlined"
                onClick={handleBulkUpdateCategory}
                disabled={bulkProcessing}
              >
                Apply
              </Button>
            </Box>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={bulkProcessing ? <CircularProgress size={16} /> : <DeleteOutlineRounded />}
              onClick={handleBulkDelete}
              disabled={bulkProcessing}
              sx={{ ml: 1 }}
            >
              Delete
            </Button>
            <Button
              size="small"
              startIcon={<ClearRounded />}
              onClick={() => setSelectedFields(new Set())}
              sx={{ ml: 'auto' }}
            >
              Clear
            </Button>
          </Box>
        </Card>
      )}

      {fields.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <DataObjectRounded sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No fields defined
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Add Field" to create your first metafield definition
            </Typography>
          </Box>
        </Card>
      ) : (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 48 }}>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={toggleSelectAll}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 160 }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 100 }}>
                    Type
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 120 }}>
                    Category
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 80 }}>
                    Unit
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 80 }}>
                    Per Variant
                  </TableCell>
                  {activeStores.map((store) => (
                    <TableCell
                      key={store.id}
                      sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 220 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <span>Key for {storeLabel(store)}</span>
                        {activeStores.length > 1 && (
                          <Button
                            size="small"
                            variant="text"
                            disabled={copyingStore === store.id}
                            onClick={(e) => setCopyMenu({ anchorEl: e.currentTarget, targetStore: store })}
                            sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0, whiteSpace: 'nowrap' }}
                          >
                            {copyingStore === store.id ? 'Copying…' : 'Copy from ▾'}
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 600, color: 'text.secondary', width: 60 }}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFields.map((field) => (
                  <TableRow key={field.id} sx={{ '&:last-child td': { border: 0 }, bgcolor: selectedFields.has(field.id) ? 'action.selected' : 'inherit' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedFields.has(field.id)}
                        onChange={() => toggleFieldSelection(field.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {field.name}
                        </Typography>
                        {mainStore && (
                          <Tooltip title={`Copy ${storeLabel(mainStore)} key to all stores`}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyMainToAll(field)}
                              disabled={savingCell === `bulk-${field.id}`}
                              sx={{ ml: 0.5 }}
                            >
                              {savingCell === `bulk-${field.id}` ? (
                                <CircularProgress size={16} />
                              ) : (
                                <ContentCopyRounded fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={FIELD_TYPES.find((t) => t.value === field.field_type)?.label || field.field_type}
                        size="small"
                        sx={{
                          bgcolor: alpha(typeColors[field.field_type] || '#888', 0.1),
                          color: typeColors[field.field_type] || '#888',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {field.category || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {MEASUREMENT_UNITS.find((u) => u.value === field.measurement_unit)?.label || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: field.per_variant ? 'primary.main' : 'text.secondary', fontWeight: field.per_variant ? 600 : 400 }}>
                        {field.per_variant ? 'Yes' : '—'}
                      </Typography>
                    </TableCell>
                    {activeStores.map((store) => {
                      const cellKey = `${field.id}-${store.id}`;
                      const changed = cellHasChanges(field, store.id);
                      const isSaving = savingCell === cellKey;

                      return (
                        <TableCell key={store.id}>
                          <Autocomplete
                            freeSolo
                            forcePopupIcon={false}
                            clearOnBlur={false}
                            size="small"
                            options={metafieldOptions[store.id] || []}
                            loading={loadingStoreOptions === store.id}
                            value={mappingEdits[field.id]?.[store.id] ?? ''}
                            inputValue={mappingEdits[field.id]?.[store.id] ?? ''}
                            onInputChange={(_, newInput) => {
                              handleMappingChange(field.id, store.id, newInput);
                            }}
                            onChange={(_, newValue) => {
                              const val =
                                typeof newValue === 'string'
                                  ? newValue
                                  : newValue?.value || '';
                              handleMappingChange(field.id, store.id, val);
                            }}
                            getOptionLabel={(option) =>
                              typeof option === 'string' ? option : option.value
                            }
                            isOptionEqualToValue={(option, value) => {
                              const optVal = typeof option === 'string' ? option : option.value;
                              const valStr = typeof value === 'string' ? value : value?.value || '';
                              return optVal === valStr;
                            }}
                            filterOptions={(options, params) => {
                              const filtered = metafieldFilter(options, params);
                              const { inputValue } = params;
                              const exists = options.some((opt) =>
                                (typeof opt === 'string' ? opt : opt.value) === inputValue
                              );
                              if (inputValue !== '' && !exists) {
                                filtered.push({
                                  value: inputValue,
                                  label: `Create "${inputValue}"`,
                                  namespace: inputValue.includes('.') ? inputValue.split('.')[0] : 'custom',
                                  type: 'Create new',
                                  isNew: true,
                                });
                              }
                              return filtered;
                            }}
                            selectOnFocus
                            handleHomeEndKeys
                            onOpen={() => loadMetafieldOptions(store.id)}
                            renderOption={(props, option) => {
                              const val = typeof option === 'string' ? option : option.value;
                              const subtitle =
                                typeof option === 'string'
                                  ? 'Use this key'
                                  : option.type || option.namespace || '';
                              const isNew = typeof option !== 'string' && option.isNew;
                              return (
                                <li {...props}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {isNew ? option.label : val}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {isNew ? 'Create and link this metafield key' : subtitle}
                                    </Typography>
                                  </Box>
                                </li>
                              );
                            }}
                            componentsProps={{
                              paper: { sx: { maxHeight: 260 } },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="e.g. custom.seat_depth"
                                size="small"
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {changed && (
                                        <InputAdornment position="end">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleSaveCell(field.id, store.id)}
                                            disabled={isSaving}
                                            sx={{ color: 'primary.main' }}
                                          >
                                            {isSaving ? (
                                              <CircularProgress size={16} color="inherit" />
                                            ) : (
                                              <SaveRounded fontSize="small" />
                                            )}
                                          </IconButton>
                                        </InputAdornment>
                                      )}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    fontSize: '0.85rem',
                                  },
                                }}
                              />
                            )}
                          />
                        </TableCell>
                      );
                    })}
                    <TableCell align="center">
                      <Tooltip title="Delete field">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(field.id)}
                          disabled={deleting === field.id}
                          sx={{ color: 'error.main' }}
                        >
                          {deleting === field.id ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <DeleteOutlineRounded fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Add Field Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setError('');
          setNewName('');
          setNewType('text');
          setNewCategory('');
          setNewMeasurementUnit('');
          setNewPerVariant(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add Field</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Field Name"
            placeholder="e.g. Seat Depth"
            value={newName}
            onChange={(e) => {
              setNewName(e.target.value);
              setError('');
            }}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            error={!!error}
            helperText={error}
          />
          <TextField
            select
            label="Field Type"
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          >
            {FIELD_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Category"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          >
            {FIELD_CATEGORIES.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Measurement Unit"
            value={newMeasurementUnit}
            onChange={(e) => setNewMeasurementUnit(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            helperText="Auto-converts to the paired unit (e.g. Yards ↔ Meters)"
          >
            {MEASUREMENT_UNITS.map((u) => (
              <MenuItem key={u.value} value={u.value}>
                {u.label}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={newPerVariant}
                onChange={(e) => setNewPerVariant(e.target.checked)}
              />
            }
            label="Per-variant values (one value per variant instead of one per product)"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setDialogOpen(false);
              setError('');
              setNewName('');
              setNewType('text');
              setNewCategory('');
              setNewMeasurementUnit('');
              setNewPerVariant(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? <CircularProgress size={20} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* "Copy from store" menu — lists the other stores to copy keys FROM */}
      <Menu
        anchorEl={copyMenu?.anchorEl}
        open={Boolean(copyMenu)}
        onClose={() => setCopyMenu(null)}
      >
        <MenuItem disabled sx={{ fontSize: '0.75rem', opacity: 0.7 }}>
          Copy keys into “{storeLabel(copyMenu?.targetStore)}” from:
        </MenuItem>
        {activeStores
          .filter((s) => s.id !== copyMenu?.targetStore?.id)
          .map((s) => (
            <MenuItem key={s.id} onClick={() => handleCopyFromStore(s, copyMenu.targetStore)}>
              {storeLabel(s)}
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
};

export default Fields;
